import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import posthog from 'posthog-js'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import Loader from 'containers/loader/loader'
import { getStoryHistory, HistoryStoryItem } from '../../redux/actions/historyAction'
import { getStory } from '../../redux/actions/storyAction'
import { localizedLevelLabel } from '../story-card/constants'
import { PlanChangeLink } from '../../utils/constant'
import { INTRODUCTION, STORIES } from '../../routes/routeConstant'
import { RESET_QUIZ, CLEAR_QUESTION_ANSWER_LIST } from '../../redux/types'
import store from '../../redux/store'
import { formatDate } from '../../i18n/format'
import { chineseLang } from '../../i18n/chinese'
import { storyTitleText } from '../../i18n/localizedContent'
import './history.css'

interface IHistoryProps {
  credentials?: any
}

// Same page size as the apps.
const PER_PAGE = 20
// The most any plan keeps (Pro and the staff tiers). Below it, the end of the
// list carries the Pro upsell, as it does in the apps.
const TOP_PLAN_LIMIT = 30

const richTextToString = (value: any): string => {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (Array.isArray(value)) return value.map((v) => v?.text || '').join(' ').trim()
  return ''
}

// en: 'Oct 20, 2026'.
const DAY_TITLE: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }

/** Groups entries by the day they were last opened, newest first, like the apps. */
export const groupByDay = (items: HistoryStoryItem[]) => {
  const groups: { key: string; title: string; items: HistoryStoryItem[] }[] = []
  items.forEach((item) => {
    const date = item.last_seen_at || item.opened_at
    // Group on the calendar day, not on the formatted title.
    const key = date ? moment(date).format('YYYY-MM-DD') : 'earlier'
    const group = groups.find((g) => g.key === key)
    if (group) group.items.push(item)
    else {
      const title = date ? formatDate(moment(date).toDate(), DAY_TITLE) : i18n.t('dashboard:history.earlier')
      groups.push({ key, title, items: [item] })
    }
  })
  return groups
}

/**
 * The row title: the learner-language title when the API sent one, else the English, else the
 * Chinese title, else a placeholder.
 */
export const historyRowTitle = (item: any, character?: string | null): { text: string; lang: string } => {
  const english = richTextToString(item?.english_title)
  const picked = storyTitleText({ ...item, englishTitle: english })
  if (picked.text) return { text: picked.text, lang: picked.lang }
  const chinese = richTextToString(item?.title)
  if (chinese) return { text: chinese, lang: chineseLang(character) }
  return { text: i18n.t('dashboard:history.untitled'), lang: '' }
}

/**
 * The learner's own history: the last lessons they studied, as many as their
 * plan keeps (Weekly and Standard 5, Pro 30). Mirrors the apps' "Past lessons"
 * screen, which opens from the same clock icon.
 */
const History: React.FC<IHistoryProps> = ({ credentials }: any) => {
  const { t } = useTranslation('dashboard')
  const navigate = useNavigate()
  const { memberId } = credentials || {}
  const character = credentials?.customFields?.character

  const [items, setItems] = useState<HistoryStoryItem[]>([])
  const [maxLimit, setMaxLimit] = useState<number | null>(null)
  const [page, setPage] = useState(1)
  const [phase, setPhase] = useState<'loading' | 'loaded' | 'failed'>('loading')
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [openingId, setOpeningId] = useState<string | null>(null)

  const loadFirstPage = async () => {
    setPhase('loading')
    const result = await getStoryHistory({ page: 1, per_page: PER_PAGE })
    if (!result) {
      setPhase('failed')
      return
    }
    setItems(result.items)
    setMaxLimit(result.maxLimit)
    setHasMore(result.items.length === PER_PAGE)
    setPage(1)
    setPhase('loaded')
  }

  useEffect(() => {
    if (!memberId) return
    loadFirstPage()
  }, [memberId])

  const onLoadMore = async () => {
    setLoadingMore(true)
    const next = page + 1
    const result = await getStoryHistory({ page: next, per_page: PER_PAGE })
    if (result) {
      const known = new Set(items.map((i) => i.id))
      setItems((prev) => [...prev, ...result.items.filter((i) => !known.has(i.id))])
      setHasMore(result.items.length === PER_PAGE)
      setPage(next)
    } else {
      setHasMore(false)
    }
    setLoadingMore(false)
  }

  const onOpen = async (item: HistoryStoryItem) => {
    if (openingId) return
    setOpeningId(item.id)
    try {
      store.dispatch({ type: RESET_QUIZ })
      store.dispatch({ type: CLEAR_QUESTION_ANSWER_LIST })
      await getStory(item.id, (item.level || 'beginner').toLowerCase())
      navigate(INTRODUCTION)
    } catch (err) {
      console.error('Failed to load story from history:', err)
    } finally {
      setOpeningId(null)
    }
  }

  const onUpgrade = (placement: string) => {
    posthog.capture('history_upgrade_clicked', { placement })
    navigate(PlanChangeLink)
  }

  // Free keeps no lessons, so "nothing here yet" would be untrue: show the upsell instead.
  const lockCard = (placement: 'locked' | 'end_of_list') => (
    <div className='history-card history-upsell'>
      <i className='fa fa-lock history-empty-icon' aria-hidden='true' />
      <h2 className='history-empty-title'>{t('history.upsellTitle')}</h2>
      <p className='history-empty-body'>{t('history.upsellBody', { limit: String(TOP_PLAN_LIMIT) })}</p>
      <button className='button-2 w-button green-btn-class' onClick={() => onUpgrade(placement)}>
        {t('history.upgrade')}
      </button>
    </div>
  )

  const emptyState = (
    <div className='history-empty'>
      <i className='fa fa-history history-empty-icon' aria-hidden='true' />
      <h2 className='history-empty-title'>{t('history.emptyTitle')}</h2>
      <p className='history-empty-body'>
        {maxLimit ? t('history.emptyBodyLimit', { limit: String(maxLimit) }) : t('history.emptyBody')}
      </p>
      <button className='button-2 w-button green-btn-class' onClick={() => navigate(STORIES)}>
        {t('history.readToday')}
      </button>
    </div>
  )

  const renderList = () => (
    <>
      {groupByDay(items).map((group) => (
        <div className='history-day' key={group.key}>
          <p className='history-day-title'>{group.title}</p>
          {group.items.map((item) => {
            const title = historyRowTitle(item, character)
            // Same detailed label as the lesson cards (Beginner 1, Beginner 2, ...).
            const levelLabel = localizedLevelLabel(item.story_level, item.level_display)
            const meta = [levelLabel, item.is_read ? t('history.finished') : null].filter(Boolean).join(' · ')
            return (
              <button
                key={`${item.id}-${item.last_seen_at}`}
                className='history-card history-row'
                onClick={() => onOpen(item)}
                disabled={openingId !== null}
              >
                <span className='history-row-text'>
                  <span className='history-row-title' lang={title.lang || undefined}>
                    {title.text}
                  </span>
                  {meta && <span className='history-row-meta'>{meta}</span>}
                </span>
                <i
                  className={openingId === item.id ? 'fa fa-spinner fa-spin' : 'fa fa-chevron-right'}
                  aria-hidden='true'
                />
              </button>
            )
          })}
        </div>
      ))}
      {hasMore ? (
        <div className='text-center'>
          {loadingMore ? (
            <Loader />
          ) : (
            <button className='history-load-more' onClick={onLoadMore}>
              {t('history.loadMore')}
            </button>
          )}
        </div>
      ) : (
        maxLimit !== null && maxLimit < TOP_PLAN_LIMIT && lockCard('end_of_list')
      )}
    </>
  )

  const renderContent = () => {
    if (phase === 'loading') return <Loader />
    if (phase === 'failed') {
      return (
        <div className='history-empty'>
          <h2 className='history-empty-title'>{t('history.failed')}</h2>
          <button className='button-2 w-button green-btn-class' onClick={loadFirstPage}>
            {t('history.tryAgain')}
          </button>
        </div>
      )
    }
    if (items.length === 0) return maxLimit === 0 ? lockCard('locked') : emptyState
    return renderList()
  }

  return (
    <div className='correction-div-main'>
      <div className='section-correction-body w-container history-page'>
        <div className='section-correction-header'>
          <div className='w-row'>
            <div className='w-col w-col-12 mb-20'>
              <span className='correction-title'>{t('history.title')}</span>
            </div>
          </div>
        </div>
        {renderContent()}
      </div>
    </div>
  )
}

const mapStateToProps = (state: any) => ({
  credentials: state.user.credentials,
})

export default connect(mapStateToProps)(History)
